/**
 * ShareInviteSheet — Path A inline share sheet (Feb 2026)
 * ========================================================
 *
 * Renders the four channel affordances (SMS, WhatsApp, Email, Copy
 * link) using the pre-built `share_messages` returned by
 * POST /api/work-mode/students/{id}/invite.
 *
 * Truth-fix doctrine:
 *   • This sheet is OPENED AFTER a token-mint endpoint returns
 *     `delivery: "manual_share"`. The header copy is literal —
 *     "Share to send" — never "Sent".
 *   • The actual send happens via the device's native sharer:
 *     `sms:` / `whatsapp://send` / `mailto:` / clipboard.
 *   • No provider call. No "delivered" claim.
 */
import { toast } from "sonner";
import { X, MessageSquare, MessageCircle, Mail, Link2 } from "lucide-react";

export default function ShareInviteSheet({ open, onClose, invite }) {
  if (!open || !invite) return null;
  const messages = invite.share_messages || {};
  const phone = (invite.student_phone || "").replace(/[^\d+]/g, "");
  const email = invite.student_email || "";
  const inviteUrl = invite.invite_url || "";
  const pin = invite.invite_pin || "";

  const openSMS = () => {
    if (!phone) {
      toast.error("No phone on file — copy the link instead");
      return;
    }
    const body = encodeURIComponent(messages.sms || `${inviteUrl} PIN ${pin}`);
    window.location.href = `sms:${phone}?&body=${body}`;
  };
  const openWhatsApp = () => {
    const body = encodeURIComponent(messages.whatsapp || `${inviteUrl} PIN ${pin}`);
    const target = phone ? phone.replace(/^\+/, "") : "";
    window.location.href = target
      ? `https://wa.me/${target}?text=${body}`
      : `https://wa.me/?text=${body}`;
  };
  const openEmail = () => {
    if (!email) {
      toast.error("No email on file — copy the link instead");
      return;
    }
    const subject = encodeURIComponent(messages.email_subject || "Your driving setup");
    const body = encodeURIComponent(messages.email_body || `${inviteUrl}\nPIN: ${pin}`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${inviteUrl}\nPIN: ${pin}`);
      toast.success("Link copied · paste into any app");
    } catch {
      toast.error("Couldn't copy — long-press the link below to copy manually");
    }
  };

  return (
    <div
      data-testid="share-invite-sheet"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="flex-1">
            <h2 className="font-extrabold text-base text-slate-900">
              Share to send
            </h2>
            <p
              data-testid="share-invite-truth-note"
              className="text-xs text-slate-500"
            >
              The new code is ready. Pick a channel — your device sends it.
            </p>
          </div>
          <button
            data-testid="share-invite-close-btn"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-4 grid grid-cols-2 gap-3">
          <ChannelBtn testid="share-sms"      icon={MessageSquare} label="SMS"      hint={phone || "no phone"} onClick={openSMS} />
          <ChannelBtn testid="share-whatsapp" icon={MessageCircle} label="WhatsApp" hint={phone || "open chat"} onClick={openWhatsApp} />
          <ChannelBtn testid="share-email"    icon={Mail}          label="Email"    hint={email || "no email"} onClick={openEmail} />
          <ChannelBtn testid="share-copy"     icon={Link2}         label="Copy link" hint="paste anywhere" onClick={copyLink} />
        </div>
        <div className="px-5 pb-5">
          <div className="text-[11px] text-slate-500 bg-slate-50 rounded-xl px-3 py-2">
            <span className="font-mono font-bold">{inviteUrl}</span>
            {pin && <span className="ml-2">· PIN <span className="font-mono font-bold">{pin}</span></span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChannelBtn({ testid, icon: Icon, label, hint, onClick }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="rounded-2xl ring-1 ring-slate-300 hover:ring-blue-400 hover:bg-blue-50 px-4 py-4 text-left transition"
    >
      <Icon className="w-5 h-5 mb-2 text-blue-600" />
      <div className="text-sm font-bold text-slate-900">{label}</div>
      <div className="text-[11px] text-slate-500 truncate">{hint}</div>
    </button>
  );
}
